// PostHog Cloud EU behind the first-party reverse proxy (ad-blocker
// resilience). Everyone is measured; consent controls STORAGE, not capture:
//   - EU-regime visitors (see lib/region.js) start with in-memory
//     persistence and upgrade to localStorage+cookie when they accept.
//   - Non-EU visitors get localStorage+cookie immediately, no banner.
//   - A stored "declined" pins memory persistence for anyone.
// GTM/GA4 is independent: hardcoded in public/index.html for every visitor;
// trackPageview feeds it SPA route changes via the dataLayer.
// The Meta pixel lives here too, for the Instagram funnel: it is loaded from
// this module rather than pasted into index.html so it can obey the consent
// regime above and so its event ids are generated by our code (see
// trackMetaEvent).

import { getConsent, subscribeConsent } from "./consent";
import { experimentProps } from "./experiment";
import { isEURegime } from "./region";

const KEY = process.env.REACT_APP_POSTHOG_KEY;
const HOST = process.env.REACT_APP_POSTHOG_HOST || "https://eu.i.posthog.com";

const COOKIE_MODE = "localStorage+cookie";
const MEMORY_MODE = "memory";

function initialPersistence() {
  const consent = getConsent();
  if (consent === "accepted") return COOKIE_MODE;
  if (consent === "declined") return MEMORY_MODE;
  return isEURegime() ? MEMORY_MODE : COOKIE_MODE;
}

let client = null;
let loading = null;

function loadPostHog() {
  if (!KEY) return Promise.resolve(null);
  if (client) return Promise.resolve(client);
  if (!loading) {
    loading = import("posthog-js")
      .then(({ default: posthog }) => {
        posthog.init(KEY, {
          api_host: HOST,
          // api_host goes through our own reverse proxy (ad-blocker
          // resilience); ui_host keeps toolbar/app links on the real UI.
          ui_host: "https://eu.posthog.com",
          // SPA: pageviews are captured manually on route change.
          capture_pageview: false,
          capture_pageleave: true,
          persistence: initialPersistence(),
        });
        // The legacy consent-gated flow persisted an opt-out flag for
        // visitors who withdrew consent; capture is unconditional now.
        if (posthog.has_opted_out_capturing())
          posthog.opt_in_capturing({ captureEventName: null });
        client = posthog;
        return client;
      })
      .catch(() => {
        loading = null;
        return null;
      });
  }
  return loading;
}

function clearPostHogStorage() {
  try {
    Object.keys(localStorage)
      .filter((k) => k.startsWith("ph_") || k.startsWith("__ph_opt_in_out"))
      .forEach((k) => localStorage.removeItem(k));
  } catch {}
  try {
    document.cookie.split(";").forEach((entry) => {
      const name = entry.split("=")[0].trim();
      if (name.startsWith("ph_")) {
        document.cookie = `${name}=; max-age=0; path=/`;
        document.cookie = `${name}=; max-age=0; path=/; domain=.${window.location.hostname}`;
      }
    });
  } catch {}
}

// ---------------------------------------------------------------------------
// Meta pixel (Instagram ads). See
// docs/superpowers/specs/2026-08-22-meta-ads-ig-funnel-design.md.

const META_PIXEL_ID = process.env.REACT_APP_META_PIXEL_ID;
const META_SCRIPT_SRC = "https://connect.facebook.net/en_US/fbevents.js";

// Unlike PostHog and GA4 the pixel is consent-gated, because it has no
// cookieless mode: every event it sends writes and reads the first-party _fbp
// cookie. So it rides the same gate as PostHog *storage* — present when
// persistence would be localStorage+cookie, absent otherwise. The visitors
// this excludes are not lost: each of the two standard events also travels
// server-side through the Conversions API, which is the path that survives
// this gate, ad blockers and the phone-to-desktop hop all at once.
function metaPixelAllowed() {
  return Boolean(META_PIXEL_ID) && initialPersistence() === COOKIE_MODE;
}

// Meta's official snippet, unminified. The stub queues calls under its own
// arguments objects until fbevents.js swaps in callMethod, so nothing fired
// before the script lands is dropped.
function installFbq() {
  if (window.fbq) return;
  const fbq = function () {
    if (fbq.callMethod) fbq.callMethod.apply(fbq, arguments);
    else fbq.queue.push(arguments);
  };
  fbq.push = fbq;
  fbq.loaded = true;
  fbq.version = "2.0";
  fbq.queue = [];
  window.fbq = fbq;
  if (!window._fbq) window._fbq = fbq;
  const script = document.createElement("script");
  script.async = true;
  script.src = META_SCRIPT_SRC;
  document.head.appendChild(script);
}

let metaPixelStarted = false;

function loadMetaPixel() {
  if (!metaPixelAllowed()) return null;
  if (!metaPixelStarted) {
    metaPixelStarted = true;
    installFbq();
    window.fbq("init", META_PIXEL_ID);
    // The PageView is what carries _fbc/_fbp for the click that just landed;
    // Meta needs it to attribute the standard events that follow to the ad.
    window.fbq("track", "PageView");
  }
  return window.fbq;
}

function clearMetaPixelStorage() {
  try {
    ["_fbp", "_fbc"].forEach((name) => {
      document.cookie = `${name}=; max-age=0; path=/`;
      document.cookie = `${name}=; max-age=0; path=/; domain=.${window.location.hostname}`;
    });
  } catch {}
}

// A conversion sent by the pixel and by our server are the same conversion,
// and Meta only knows that if both carry the same (event_name, event_id).
// Rather than mint an id here and ship it to the backend, both sides DERIVE it
// from the one thing they already agree on: the email address. Same input,
// same hash, no round trip, and nothing about the lead's resume token ever has
// to reach the browser. The backend recomputes the id from the address it
// stored and ignores any event_id in the request body, so there is exactly one
// definition of the scheme and no way for the two halves to drift.
//
// Deduplication comes free from the derivation: submitting the same address
// twice yields the same id, so the pixel copy inherits the backend's "one lead
// per person, not one per tap" behaviour with no client-side state at all.
//
// Normalisation is part of the contract — trim then lowercase — because
// "Producer@Example.com " and "producer@example.com" are one person and must
// hash to one id. See analytics.test.js, which pins the hex for a known
// address so this can't quietly change.
async function sha256Hex(value) {
  const bytes = new TextEncoder().encode(value);
  const digest = await globalThis.crypto.subtle.digest("SHA-256", bytes);
  return Array.from(new Uint8Array(digest))
    .map((byte) => byte.toString(16).padStart(2, "0"))
    .join("");
}

// Resolves to null when the hash cannot be computed, which callers pass
// straight to trackMetaEvent: the event is still worth sending un-deduplicated
// (see trackMetaEvent). crypto.subtle exists only in a secure context, so it
// is absent on plain-http dev origins like http://192.168.x.x:3000 — and
// production, being https, always has it.
export async function metaEventId(scope, email) {
  const address = String(email ?? "").trim().toLowerCase();
  if (!address) return null;
  try {
    if (!globalThis.crypto?.subtle) return null;
    return `${scope}_${await sha256Hex(address)}`;
  } catch {
    return null;
  }
}

// One pixel event. `eventId` is the deduplication key shared with the
// server-side copy (see metaEventId); a null/absent id sends the event without
// an eventID, which is the right trade for an event no server path can emit
// and for the dev origins where metaEventId cannot hash — an un-deduplicated
// conversion still beats a missing one. Best-effort throughout: a missing
// pixel, a blocked script or a throwing fbq must never break the flow being
// measured.
export function trackMetaEvent(name, eventId, params = {}) {
  const fbq = loadMetaPixel();
  if (!fbq) return;
  try {
    if (eventId) fbq("track", name, params, { eventID: eventId });
    else fbq("track", name, params);
  } catch {}
}

let lastPath = null;

// First-touch UTM capture. A visitor's landing URL is the only reliable place
// to read utm_source/utm_medium/utm_campaign: react-router Links replace the
// query string, and the plain anchors to /download drop it via a full page
// load. sessionStorage survives both; a module variable would not survive
// the full page load. Write-once so a later internal navigation (no UTMs)
// can't clobber the campaign that actually brought the visitor in.
const UTM_STORAGE_KEY = "tm_utm";
// utm_content/utm_term carry the per-post breakdown (see docs/marketing):
// without them every Instagram visit collapses into one undifferentiated
// campaign and there is nothing to compare.
const UTM_PARAM_NAMES = [
  "utm_source",
  "utm_medium",
  "utm_campaign",
  "utm_content",
  "utm_term",
];

// The landing referrer, recorded next to the UTMs and under the same
// write-once rule. PostHog derives $entry_referring_domain from the session,
// and a session that starts mid-visit reads our own domain as its referrer:
// under memory persistence (EU visitors before they accept) the distinct_id
// and session id live in RAM, so every full page load — the plain /download
// anchors, the return from checkout.stripe.com — starts a brand new session
// whose referrer is voleor.com or Stripe. That is where 22 of 213 sessions
// and the only purchase of Aug 7-18 lost their real source. sessionStorage
// is per-tab and survives those loads, so it can carry the true first touch
// across them and back onto every event.
const REFERRER_STORAGE_KEY = "tm_referrer";

// Same-tab continuations, not real traffic sources: a referrer pointing at
// one of these means we are looking at a page load inside an ongoing visit.
const SELF_REFERRAL_HOSTS = ["voleor.com", "checkout.stripe.com"];

function isSelfReferral(hostname) {
  return SELF_REFERRAL_HOSTS.some(
    (host) => hostname === host || hostname.endsWith(`.${host}`),
  );
}

function captureFirstTouchReferrer() {
  try {
    if (sessionStorage.getItem(REFERRER_STORAGE_KEY)) return;
    const referrer = document.referrer || "";
    // No referrer at all is a genuine direct/typed-in landing, worth
    // recording so a later internal load cannot overwrite it with our own
    // domain. A self-referral this early means storage was wiped mid-visit
    // (or the tab was restored), and we have nothing truthful to store.
    if (!referrer) {
      sessionStorage.setItem(REFERRER_STORAGE_KEY, "$direct");
      return;
    }
    let hostname;
    try {
      hostname = new URL(referrer).hostname;
    } catch {
      return;
    }
    if (isSelfReferral(hostname)) return;
    sessionStorage.setItem(REFERRER_STORAGE_KEY, hostname);
  } catch {
    // Private browsing / disabled storage: degrade to "nothing captured".
  }
}

function captureFirstTouchUtms() {
  try {
    // Already recorded this session: the first touch wins, full stop.
    if (sessionStorage.getItem(UTM_STORAGE_KEY)) return;
    const params = new URLSearchParams(window.location.search);
    const utms = {};
    let hasAny = false;
    UTM_PARAM_NAMES.forEach((name) => {
      // A present-but-empty param ("?utm_source=") is absent, same as a
      // missing one, otherwise it would count toward hasAny and then get
      // locked in as a permanent blank for the session.
      const value = params.get(name) || null;
      utms[name] = value;
      if (value) hasAny = true;
    });
    // Nothing worth storing: leave the key absent, not five nulls, so a
    // real campaign touch later in the same tab can still be recorded.
    if (hasAny) sessionStorage.setItem(UTM_STORAGE_KEY, JSON.stringify(utms));
  } catch {
    // Private browsing / disabled storage: degrade to "nothing captured".
  }
}

function captureFirstTouch() {
  captureFirstTouchUtms();
  captureFirstTouchReferrer();
}

// The first touch as event properties, prefixed so they never collide with
// PostHog's own session-derived $entry_* fields: those stay as they are, and
// these are the ones to break conversions down by.
function firstTouchProps() {
  const utms = getStoredUtms();
  const props = {};
  let hasAny = false;
  UTM_PARAM_NAMES.forEach((name) => {
    const value = utms[name] ?? null;
    if (value) {
      props[`ft_${name.replace("utm_", "")}`] = value;
      hasAny = true;
    }
  });
  try {
    const referrer = sessionStorage.getItem(REFERRER_STORAGE_KEY);
    if (referrer) {
      props.ft_referring_domain = referrer;
      hasAny = true;
    }
  } catch {}
  return hasAny ? props : null;
}

// Registered as super properties so every later event carries them, and
// set_once on the person so the first touch survives the whole lifetime.
// Re-applied on each pageview rather than once at init: under memory
// persistence PostHog's own registration is wiped by a full page load, and
// a campaign touch can also land after the client was already loaded.
let appliedFirstTouch = null;

function applyFirstTouch(ph) {
  const props = firstTouchProps();
  if (!props) return;
  const fingerprint = JSON.stringify(props);
  if (fingerprint === appliedFirstTouch) return;
  appliedFirstTouch = fingerprint;
  ph.register(props);
  ph.setPersonProperties(undefined, props);
}

// The A/B arm, registered the same way and for the same reason as the first
// touch above: PostHog wipes its own registrations on a full page load under
// memory persistence, and `purchase` fires on exactly that — a fresh load
// coming back from Stripe. Read from our own storage on every send instead of
// being registered once at init.
//
// Silent for anyone not in the experiment (see experiment.js), so a /docs
// visitor's events stay unlabelled rather than joining a group they were never
// shown.
let appliedExperiment = null;

function applyExperiment(ph) {
  const props = experimentProps();
  if (!props) return;
  const fingerprint = JSON.stringify(props);
  if (fingerprint === appliedExperiment) return;
  appliedExperiment = fingerprint;
  ph.register(props);
}

// Reads the UTMs persisted by captureFirstTouchUtms, for callers (the lead
// capture form) that need them after the query string is long gone.
export function getStoredUtms() {
  const empty = {};
  UTM_PARAM_NAMES.forEach((name) => {
    empty[name] = null;
  });
  try {
    const raw = sessionStorage.getItem(UTM_STORAGE_KEY);
    if (!raw) return empty;
    const parsed = JSON.parse(raw);
    const utms = {};
    UTM_PARAM_NAMES.forEach((name) => {
      utms[name] = parsed[name] ?? null;
    });
    return utms;
  } catch {
    return empty;
  }
}

export function trackPageview(path) {
  // Route effects fire twice in dev StrictMode; only capture real changes.
  if (path === lastPath) return;
  lastPath = path;
  // The first call of a visit always passes the guard above, which is
  // exactly when a landing URL still carries its campaign params.
  captureFirstTouch();
  loadPostHog().then((ph) => {
    if (!ph) return;
    // Before the capture, so the pageview itself already carries the source.
    applyFirstTouch(ph);
    applyExperiment(ph);
    ph.capture("$pageview", { $current_url: window.location.href });
  });
  // GTM (public/index.html) owns GA4; its pageview tag triggers on this
  // custom event — including the very first render, so the GA4 tag must
  // NOT also fire on All Pages/History Change (double-count).
  try {
    window.dataLayer?.push({
      event: "page_view",
      page_path: path,
      ...(firstTouchProps() || {}),
    });
  } catch {}
}

// Conversion events. Same dual-sink contract as trackPageview: PostHog gets
// the event directly, GA4 gets it through the GTM dataLayer (a GA4 event tag
// per name, fired by a Custom Event trigger). Names follow GA4's recommended
// set where one exists (sign_up, begin_checkout, purchase, file_download) so
// GA4 can treat them as key events without extra mapping; trial_start is ours.
export function trackEvent(name, props = {}) {
  // A conversion can be the first thing that fires after a full page load
  // (purchase on the Stripe return, file_download on the /download anchor),
  // so the first touch is re-read here rather than assumed present from a
  // pageview whose loadPostHog promise may still be in flight.
  captureFirstTouch();
  loadPostHog().then((ph) => {
    if (!ph) return;
    applyFirstTouch(ph);
    applyExperiment(ph);
    ph.capture(name, props);
  });
  try {
    window.dataLayer?.push({ event: name, ...(firstTouchProps() || {}), ...props });
  } catch {}
}

// A buy-button tap, sent to every sink at once. PostHog/GA get the GA4-named
// begin_checkout as before; Meta gets its standard InitiateCheckout so the
// ~2x-larger buy-tap audience is visible to the ads algorithm at all (until
// now Meta only ever saw the email submitters). No eventId on purpose: no
// server path fires this event, so there is nothing to deduplicate against —
// and no value either, since Adaptive Pricing means the browser does not
// know what Stripe will charge. Optimisation stays on Lead; this exists so
// an InitiateCheckout ad set is a dropdown choice later, not a deploy.
export function trackBeginCheckout(props = {}) {
  trackEvent("begin_checkout", props);
  trackMetaEvent("InitiateCheckout", null, props.source ? { content_name: props.source } : {});
}

// Fire an event at most once per key. Guards against React StrictMode double
// effects and, for purchase, against a page refresh replaying the conversion:
// the Stripe session_id in the return URL is the natural idempotency key.
export function trackEventOnce(storageKey, name, props = {}) {
  try {
    const seen = `tm_evt_${storageKey}`;
    if (localStorage.getItem(seen)) return;
    localStorage.setItem(seen, "1");
  } catch {
    // Private mode / storage blocked: fall through and send it anyway. A
    // duplicate conversion beats a silently missing one.
  }
  trackEvent(name, props);
}

// Staff browsing their own site drowns a small site's numbers (86% of our
// pageviews in July). IP rules only cover the networks you remember to list,
// so we key off the admin role instead: it follows the person to any device.
// PostHog gets the $internal_or_test_user person property (its "Internal /
// Test users" cohort excludes it); GA4 gets traffic_type=internal, which the
// property's already-active "Internal Traffic" data filter drops. Note the
// first pageview of a session still lands: auth resolves after it fires.
let internalMarked = false;

export function markInternalUser() {
  if (internalMarked) return;
  internalMarked = true;
  loadPostHog().then((ph) => {
    if (ph) ph.setPersonProperties({ $internal_or_test_user: true });
  });
  try {
    // Persists in the dataLayer, so every later GA4 tag reads it.
    window.dataLayer?.push({ traffic_type: "internal" });
  } catch {}
}

export function initAnalytics() {
  // Sweep storage left by the legacy consent-gated flow (or by cookie mode
  // before a withdrawal on another tab) for visitors whose stored choice
  // now means "no PostHog storage".
  if (getConsent() === "declined") clearPostHogStorage();
  loadMetaPixel();

  subscribeConsent((consent) => {
    if (consent === "accepted") {
      loadMetaPixel();
      loadPostHog().then((ph) => {
        // Live upgrade: the in-memory session keeps its distinct_id and
        // starts persisting it. Re-check consent: it may have flipped to
        // declined while the posthog chunk was still loading.
        if (ph && getConsent() === "accepted")
          ph.set_config({ persistence: COOKIE_MODE });
      });
    } else if (consent === "declined") {
      if (client) {
        client.set_config({ persistence: MEMORY_MODE });
        // Withdrawal: forget the device identity…
        client.reset();
      }
      // …then sweep whatever cookie mode persisted.
      clearPostHogStorage();
      // Withdrawal has to reach the pixel too. `consent revoke` stops fbq
      // sending anything further in this pageload (the script itself cannot
      // be unloaded), and the sweep removes the cookies it already wrote.
      if (window.fbq) {
        try {
          window.fbq("consent", "revoke");
        } catch {}
      }
      clearMetaPixelStorage();
    }
    // consent === null (banner reopened from the privacy page): keep the
    // current mode until the visitor decides again.
  });
}
